import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import {
  Descriptions,
  Typography,
  Select,
  InputNumber,
  Switch,
  Input,
  Tag,
  message as antMessage,
  Spin,
  DatePicker
} from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  amendmentModificationColor,
  exemptionStatusColor
} from '../../utils/constants/tagColors';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import checkExemption, {
  exemptionsValues
} from '../../routes/quotes/checkExemption';
import { maxModifiedValue } from '../../routes/contracts/maxModifiedValue';
import { formatValue } from '../../utils/formatValue';
import useQuotesContext from '../../routes/quotes/QuotesContext';
import PatchModalExemption from '../../routes/contracts/PatchModalExemption';
import PatchExtraValueModal from '../../routes/contracts/PatchExtraValueModal';
import authorizedEdit from '../../utils/constants/authorizedEdit';
import {
  getMobilityOptions,
  getDeductibleOptions,
  getRcProOptions,
  getBusinessLossOptions,
  marketOptions
} from '../../utils/milaOptions';

import PatchMRIWKAddressModal from '../../routes/contracts/PatchMRIWKAddressModal';

const statusEditAuthorized = ['AWAITING_INFORMATION', 'ACCEPTED'];

const typeFieldUnauthorizedEdit = {
  rcphx: ['deductible_amount']
};

const { Option } = Select;

const DescriptionList = ({
  title,
  data,
  bordered,
  size,
  activeTab,
  values,
  exemptions,
  type,
  getContract,
  schema,
  setVisibleModal,
  setTempData
}) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { message } = useErrorMessage();
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [modifiedValue, setModifiedValue] = useState();
  const [defaultActivities, setDefaultActivities] = useState();
  const [firstChange, setFirstChange] = useState(false);
  const [newExemptions, setNewExemptions] = useState([]);
  const [key, setKey] = useState();
  const {
    codesNaf,
    activitiesAsso,
    activitiesHiscox = []
  } = useQuotesContext();

  const liveRcphxActivitiesDetails = useMemo(
    () =>
      activitiesHiscox.reduce((acc, { type, name }) => {
        if (!acc[type]) acc[type] = [];
        acc[type].push({ name });
        return acc;
      }, {}),
    [activitiesHiscox]
  );
  const [extraKey, setExtraKey] = useState();
  const [modalKey, setModalKey] = useState();
  const [modalData, setModalData] = useState();
  const [modalModifExemption, setModalModifExemption] = useState(false);
  const [openExtraValueModal, setOpenExtraValueModal] = useState(false);
  const [mriwkModalOpen, setMriwkModalOpen] = useState(false);
  const [licensePlates, setLicensePlates] = useState([]);
  const [openValueModalDeductible, setOpenValueModalDeductible] =
    useState(false);

  const handleOpenMriwkModal = () => {
    setMriwkModalOpen(true);
  };

  const handleCloseMriwkModal = () => {
    setMriwkModalOpen(false);
  };

  const activitiesRCNGA = [
    'Commerce de voitures et de véhicules automobiles léger - 4511Z',
    'Commerce d’autres véhicules automobiles - 4519Z'
  ];

  const selectedField = useMemo(() => {
    if (!key || !Array.isArray(data)) {
      return null;
    }
    return data.find(
      (field) => field.key === key || field.label.split('.').pop() === key
    );
  }, [key]);

  const extra = {
    risk_typology: 'furniture_capital',
    furniture_capital: 'furniture_capital_other',
    empty_unoccupied_works: 'empty_reason'
  };

  const checkFormatDate = {
    construction_year: 'YYYY',
    last_termination_date: 'DD/MM/YYYY'
  };

  const checkFormatPicker = {
    construction_year: 'year',
    last_termination_date: ''
  };

  const modelType = () => {
    try {
      if (key === 'activities') return String;
      if (key === 'siret' || key === 'rna') {
        return schema?.customer?.[key]?.type || String;
      }
      return schema?.[key]?.type || String;
    } catch (error) {
      console.error('🔴 ERROR in modelType:', { key, error });
      return String; // Fallback sûr
    }
  };

  const mobilityOptions = getMobilityOptions();

  const assoType = 'association';

  const checkRealExemption = () =>
    values &&
    exemptions
      .filter((f) => f !== null)
      .map((ex) => {
        // EXEMPTION THAT CANNOT BE CHECKED BY THE FRONTEND
        const forcedExemptionKeys = ['address.city'];
        if (forcedExemptionKeys.includes(ex)) {
          newExemptions.push(ex);
          return true;
        }

        const checkValue = { ...values, [ex]: values[ex] };
        const typeFormatted = exemptionsValues[type]
          ? type
          : type !== assoType
            ? type?.substring(0, 3)
            : assoType;
        const check = checkExemption({
          type: typeFormatted,
          values: checkValue,
          initialContractValues: checkValue,
          codesNaf: type !== assoType ? codesNaf : activitiesAsso,
          supplier: type?.slice(-2),
          activitiesHiscox
        });
        if (check.length) {
          const test = newExemptions;
          test.push(ex);
          setNewExemptions(test);
        }
        return true;
      });

  const nextPatchContractValue = async (
    check,
    valueToSend,
    body,
    updaterKey
  ) => {
    const response = await dispatchAPI('PATCH', {
      url: `/contracts/value/${id}`,
      body: {
        value:
          key === 'buildings' ||
          key === 'sites' ||
          key === 'claims' ||
          (type === 'mriwk' && key === 'address')
            ? body
            : valueToSend,
        key: updaterKey ?? key,
        type,
        check,
        extraValue: body
      }
    });
    if (response.status === 200) {
      antMessage.success(t('tickets.messages.success'));
      setModifiedValue();
      setKey();
      setExtraKey();
      getContract();
    }
  };

  const getInitialsActivities = async () => {
    const array = [];
    const initialsActivities = await data.find(
      (i) => i.label === 'contracts.show.activities'
    )?.content;
    // eslint-disable-next-line array-callback-return
    await initialsActivities?.map((activity) => {
      array.push(activity.key);
    });
    return array;
  };

  const supplierMapping = {
    cm: 'CMAM',
    wk: 'WAKAM',
    ml: 'MILA'
  };

  const supplierFullName = supplierMapping[type?.slice(-2)] || 'SMAB';

  const deductibleOptions =
    supplierFullName === 'MILA'
      ? getDeductibleOptions()
      : [1000, 750, 500, 250, 0];

  const patchContractValue = async (body) => {
    const updater = selectedField?.updater;

    let updaterKey = key;
    let updaterValue = modifiedValue;
    if (typeof updater === 'function') {
      const { key: updatedKey, value: updatedValue } = updater(
        modifiedValue,
        values
      );

      updaterKey = updatedKey;
      updaterValue = updatedValue;
    }

    setIsLoading(true);
    try {
      let valueToSend = updaterValue ?? modifiedValue;
      if (modifiedValue === undefined)
        valueToSend = await getInitialsActivities();

      const value = { [updaterKey ?? key]: valueToSend };
      const typeFormatted =
        type !== assoType ? type?.substring(0, 3) : assoType;
      const check = checkExemption({
        type: typeFormatted,
        values: value,
        initialContractValues: values,
        codesNaf,
        supplier: type?.slice(-2),
        activitiesHiscox
      });
      if (
        (check.length && values?.exemption?.status === 'ACCEPTED') ||
        (values?.exemption?.status === 'ACCEPTED' &&
          Object.keys(value)[0] === 'sites') ||
        (values?.exemption?.status === 'ACCEPTED' &&
          value[Object.keys(value)[0]] === true) ||
        (Object.keys(value)[0] !== 'deductible_amount' &&
          value[Object.keys(value)[0]] > values[Object.keys(value)[0]] &&
          values?.exemption?.status === 'ACCEPTED') ||
        (Object.keys(value)[0] === 'deductible_amount' &&
          value[Object.keys(value)[0]] < values[Object.keys(value)[0]] &&
          values?.exemption?.status === 'ACCEPTED')
      ) {
        if (!check.length) check.push(Object.keys(value)[0]);
        setTempData({
          check: { ...check },
          key: updaterKey ?? key,
          modifiedValue: Object.keys(value)[0] === 'sites' ? body : valueToSend,
          type
        });
        setVisibleModal(true);
      } else {
        await nextPatchContractValue(check, valueToSend, body, updaterKey);
      }
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  };

  const checkExtraModal = () => {
    if (
      (extraKey === 'furniture_capital' && modifiedValue === 'FURNISHED') ||
      (extraKey === 'furniture_capital_other' && modifiedValue === 'OTHER') ||
      (extraKey === 'empty_reason' && modifiedValue === true)
    ) {
      setOpenExtraValueModal(true);
    } else {
      patchContractValue();
    }
  };

  const getOptionsActivities = () => {
    let baseActivities;
    if (type === assoType) {
      baseActivities = activitiesAsso;
    } else if (type === 'rcngawk') {
      baseActivities = activitiesRCNGA;
      return activitiesRCNGA.map((activity) => {
        return {
          value: activity,
          label: activity
        };
      });
    } else {
      baseActivities = codesNaf?.filter((code) =>
        code?.display?.[type?.slice(0, 3).toUpperCase()]?.includes(
          supplierFullName
        )
      );
    }
    return baseActivities?.map(({ naf_code, name }) => ({
      value: `${name} ${type === assoType ? '' : `- ${naf_code || ''}`}`,
      label: `${name} ${type === assoType ? '' : `- ${naf_code || ''}`}`
    }));
  };

  const hasCausedExemption = (k) => !!newExemptions?.includes(k);

  const isDifferentFromPrevious = (k) =>
    activeTab &&
    activeTab !== 'root' &&
    values?.previous_contract &&
    (values[k] !== values.previous_contract[k] ||
      (values[k] && values.previous_contract[k] === 0) ||
      (values[k] && values.previous_contract[k] === false) ||
      (values[k] && !values.previous_contract[k]));

  const checkModifiedValue = (value) => {
    if (maxModifiedValue[key] && value > maxModifiedValue[key][type]) {
      setModifiedValue(maxModifiedValue[key][type]);
    } else if (value < 0) {
      setModifiedValue(0);
    } else {
      setModifiedValue(value);
    }
  };

  const handleFirstChange = async (event) => {
    setModifiedValue(event.concat(await getInitialsActivities()));
    setFirstChange(true);
  };

  const fieldType = {
    Boolean: (
      <Switch
        checked={modifiedValue}
        checkedChildren={t('quotes.form.switch.yes')}
        unCheckedChildren={t('quotes.form.switch.no')}
        onChange={(value) => setModifiedValue(value)}
      />
    ),
    Number:
      // eslint-disable-next-line no-nested-ternary
      key && schema[key]?.enum ? (
        <Select
          style={{ width: 250 }}
          onChange={(value) => setModifiedValue(value)}
        >
          {key &&
            schema[key]?.enum &&
            schema[key]?.enum.map((enums) => (
              <Option key={enums} value={enums}>
                {t(`settings.tickets.enums.${enums}`)}
              </Option>
            ))}
        </Select>
      ) : key === 'deductible_amount' ? (
        <>
          <Select
            style={{ width: 250 }}
            onChange={(value) => {
              return setModifiedValue(value);
            }}
          >
            {deductibleOptions.map((amount) => (
              <Option value={amount}>{`${amount}€`}</Option>
            ))}
          </Select>
          <PatchExtraValueModal
            schema={schema}
            extraKey={key}
            min={0}
            patchContractValue={patchContractValue}
            openExtraValueModal={openValueModalDeductible}
            setOpenExtraValueModal={setOpenValueModalDeductible}
          />
        </>
      ) : (
        <InputNumber
          onChange={(value) => checkModifiedValue(value)}
          // max={
          //   (key && schema[key]?.enum === 'Number') ||
          //   (schema[key]?.enum === 'Mixed' && maxModifiedValue[key]?.[type])
          // }
          min={0}
        />
      ),
    MobilityGuarantee: (
      <Select
        style={{ width: 250 }}
        onChange={(value) => {
          return setModifiedValue(value);
        }}
      >
        {mobilityOptions.map((o) => (
          <Option value={o.value}>
            {t(`contracts.form.options.${o.label}`)}
          </Option>
        ))}
      </Select>
    ),
    BusinessLossGuarantee: (
      <Select
        style={{ width: 250 }}
        onChange={(value) => {
          return setModifiedValue(value);
        }}
      >
        {getBusinessLossOptions().map((o) => (
          <Option value={o.value}>
            {t(`contracts.form.options.${o.label}`)}
          </Option>
        ))}
      </Select>
    ),
    MarketValue: (
      <Select
        style={{ width: 250 }}
        onChange={(value) => {
          return setModifiedValue(value);
        }}
      >
        {marketOptions.map((o) => (
          <Option value={o}>{`${o}€`}</Option>
        ))}
      </Select>
    ),
    RcPro: (
      <Select
        style={{ width: 250 }}
        onChange={(value) => {
          return setModifiedValue(value);
        }}
      >
        {getRcProOptions().map((o) => (
          <Option value={o}>{`${o}€`}</Option>
        ))}
      </Select>
    ),
    Mixed: (
      <InputNumber
        onChange={(value) => checkModifiedValue(value)}
        // max={
        //   (key && schema[key]?.enum === 'Number') ||
        //   (schema[key]?.enum === 'Mixed' && maxModifiedValue[key]?.[type])
        // }
        min={0}
      />
    ),
    Activities: (
      <Select
        className="activities-select"
        mode="multiple"
        style={{ width: 550 }}
        options={getOptionsActivities()}
        filterOption={(input, option) =>
          formatValue(option.label).indexOf(formatValue(input)) > -1
        }
        defaultValue={defaultActivities}
        onChange={(event) =>
          firstChange ? handleFirstChange(event) : setModifiedValue(event)
        }
      />
    ),
    String:
      key && schema[key]?.enum ? (
        <Select
          style={{ width: 250 }}
          onChange={(value) => setModifiedValue(value)}
        >
          {key &&
            schema[key]?.enum &&
            schema[key]?.enum.map(
              (enums) =>
                enums !== '45x' && (
                  <Option key={enums} value={enums}>
                    {t(`settings.tickets.enums.${enums}`)}
                  </Option>
                )
            )}
        </Select>
      ) : (
        <Input onChange={(event) => setModifiedValue(event.target.value)} />
      ),
    Date: (
      <DatePicker
        format={checkFormatDate[key]}
        picker={checkFormatPicker[key]}
        style={{ minWidth: 200 }}
        disabledDate={(current) => {
          return current >= moment();
        }}
        onChange={(date) => setModifiedValue(date)}
      />
    ),
    RCNGAWK_GARAGE_CERTIFICATE: (
      <>
        {data.map((item) => {
          if (item.key === 'w_garage_certificate_number') {
            const numberOfInputs = parseInt(item.content, 10);
            return Array.from({ length: numberOfInputs }, (_, inputIndex) => (
              <Input
                key={`RCNGAWK_garage_certificate_${inputIndex}`}
                defaultValue={
                  data.find((element) => element.key === 'w_garage_certificate')
                    .content[inputIndex]?.key
                }
                onBlur={(event) => {
                  const newValue = event.target.value;
                  const baseArray =
                    licensePlates.length === 0
                      ? data
                          ?.find(
                            (element) => element.key === 'w_garage_certificate'
                          )
                          ?.content.map((obj) => obj.key) || []
                      : licensePlates;
                  const tmpArray = [...baseArray];
                  tmpArray[inputIndex] = newValue;
                  setLicensePlates(tmpArray);
                  setModifiedValue(tmpArray);
                }}
              />
            ));
          }
          return null;
        })}
      </>
    ),
    RCPHX_ACTIVITY_DETAIL: (
      <Select
        mode="multiple"
        value={modifiedValue}
        onChange={(value) => {
          setModifiedValue(value);
        }}
        style={{ width: '500px' }}
      >
        {liveRcphxActivitiesDetails[values?.activity_type || 'TECH']?.map(
          (activity) => (
            <Option
              value={activity.name}
              key={`RCPHX_activity_detail_${activity.name}`}
            >
              {activity.name}
            </Option>
          )
        )}
      </Select>
    )
  };

  useEffect(() => {
    if (exemptions) {
      (() => {
        checkRealExemption();
      })();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (values && key) {
      (() => {
        setModifiedValue(selectedField?.defaultValue ?? values[key]);
      })();
    }
    // eslint-disable-next-line
  }, [key]);

  const getFieldType = (k) => {
    try {
      if (type === 'rcngawk' && k === 'w_garage_certificate') {
        return fieldType.RCNGAWK_GARAGE_CERTIFICATE;
      }
      if (selectedField?.fieldType) {
        if (fieldType[selectedField.fieldType]) {
          return fieldType[selectedField.fieldType];
        }
      }

      if (type === 'rcphx' && k === 'activity_detail') {
        return fieldType.RCPHX_ACTIVITY_DETAIL;
      }
      if (k === 'mobility_guarantee') {
        return fieldType.MobilityGuarantee;
      }

      if (k === 'market_value') {
        return fieldType.MarketValue;
      }

      if (k === 'rc_pro_guarantee') {
        return fieldType.RcPro;
      }

      if (k === 'business_loss_guarantee') {
        return fieldType.BusinessLossGuarantee;
      }

      switch (k) {
        case 'activities':
          return fieldType.Activities;
        default: {
          const resolvedType = modelType();

          if (!fieldType[resolvedType]) {
            return <span style={{ color: 'orange' }}>Type inconnu: {k}</span>;
          }

          return fieldType[resolvedType];
        }
      }
    } catch (error) {
      return <span style={{ color: 'red' }}>Erreur champ: {k}</span>;
    }
  };

  const renderItem = (k, content, exemptionKey = null, isExemption = false) => {
    if (key === k && exemptions) {
      return (
        <>
          {getFieldType(k)}
          <CloseOutlined
            style={{
              marginLeft: 15,
              marginRight: 10,
              color: 'red'
            }}
            onClick={() => {
              setKey();
              setExtraKey();
            }}
          />
          <CheckOutlined
            style={{ color: 'green' }}
            onClick={() => {
              if (extraKey) {
                checkExtraModal();
              } else {
                patchContractValue();
              }
            }}
          />
        </>
      );
    }
    if (
      isExemption ||
      hasCausedExemption(exemptionKey || k) ||
      isDifferentFromPrevious(key || k)
    ) {
      return (
        <Tag
          color={
            hasCausedExemption(exemptionKey || k) || isExemption
              ? exemptionStatusColor.EXEMPTION
              : amendmentModificationColor.EDIT
          }
          style={{ fontSize: 16 }}
        >
          {content === 0 ? 0 : content || '-'}
        </Tag>
      );
    }
    return content === 0 ? 0 : content || '-';
  };

  const editActivities = async (defaultKey) => {
    setDefaultActivities(await getInitialsActivities());
    setModifiedValue(defaultActivities);
    setKey(defaultKey);
  };

  return (
    <Spin spinning={isLoading}>
      <PatchModalExemption
        data={modalData}
        patchContractValue={patchContractValue}
        modalModifExemption={modalModifExemption}
        setModalModifExemption={setModalModifExemption}
        setKey={setKey}
        supplier={type?.slice(-2)}
        modalKey={modalKey}
      />
      <PatchExtraValueModal
        schema={schema}
        extraKey={extraKey}
        min={0}
        patchContractValue={patchContractValue}
        openExtraValueModal={openExtraValueModal}
        setOpenExtraValueModal={setOpenExtraValueModal}
      />

      {type === 'mriwk' && key === 'address' && (
        <PatchMRIWKAddressModal
          visible={mriwkModalOpen}
          onClose={handleCloseMriwkModal}
          onPatch={patchContractValue}
          address={values?.address}
        />
      )}

      <Descriptions title={title} bordered={bordered} size={size}>
        {data
          .filter((d) => d.label)
          .map(
            ({
              content,
              label,
              span,
              trad,
              exemptionKey,
              isExemption,
              defaultValue,
              isEditable = false
            }) => {
              const splitLabel = label.split('.');
              let defaultKey = splitLabel[splitLabel.length - 1];
              return (
                <Descriptions.Item
                  label={<Trans>{t(`${trad || label}`)}</Trans>}
                  span={span || 1}
                  key={label}
                >
                  <Typography style={{ fontWeight: 700 }}>
                    {renderItem(defaultKey, content, exemptionKey, isExemption)}
                    {!typeFieldUnauthorizedEdit[type]?.includes(defaultKey) &&
                    (authorizedEdit.includes(`${defaultKey}`) || isEditable) &&
                    !key &&
                    exemptions &&
                    statusEditAuthorized.includes(values?.exemption?.status) ? (
                      <EditOutlined
                        style={{
                          marginLeft: 15,
                          color: 'purple',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          // eslint-disable-next-line react/prop-types
                          setModifiedValue(defaultValue ?? values[defaultKey]);
                          if (
                            label === 'contracts.form.surface_area_sites' ||
                            label === 'contracts.form.resident_status'
                          )
                            defaultKey = 'sites';
                          setKey(defaultKey);
                          if (extra[defaultKey]) setExtraKey(extra[defaultKey]);
                          if (
                            label === 'contracts.form.buildings' ||
                            label === 'contracts.form.sites' ||
                            label === 'contracts.form.claims' ||
                            label === 'contracts.form.surface_area_sites' ||
                            label === 'contracts.form.resident_status'
                          ) {
                            setModalKey(defaultKey);
                            setModalData([
                              ...(defaultKey === 'claims'
                                ? values[defaultKey].map((element) => ({
                                    ...element,
                                    claim_date: moment(element.claim_date)
                                  }))
                                : [...values[defaultKey]])
                            ]);
                            setModalModifExemption(true);
                          }

                          //  MRIWK ADDRESS MODAL
                          if (type === 'mriwk' && defaultKey === 'address') {
                            handleOpenMriwkModal();
                          }

                          if (defaultKey === 'activities') {
                            editActivities(defaultKey);
                          }
                        }}
                      />
                    ) : (
                      []
                    )}
                  </Typography>
                </Descriptions.Item>
              );
            }
          )}
      </Descriptions>
    </Spin>
  );
};

DescriptionList.propTypes = {
  title: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  bordered: PropTypes.bool,
  size: PropTypes.string,
  values: PropTypes.shape({
    risk_typology: PropTypes.string,
    previous_contract: PropTypes.shape({}),
    buildings: PropTypes.shape({}),
    sites: PropTypes.shape({}),
    claims: PropTypes.shape({}),
    exemption: PropTypes.shape({
      status: PropTypes.string
    })
  }),
  activeTab: PropTypes.string,
  exemptions: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string,
  getContract: PropTypes.func,
  schema: PropTypes.shape({
    customer: PropTypes.shape({})
  }),
  setVisibleModal: PropTypes.func,
  setTempData: PropTypes.func.isRequired
};

DescriptionList.defaultProps = {
  title: null,
  bordered: false,
  size: 'default',
  activeTab: 'root',
  values: null,
  exemptions: undefined,
  type: undefined,
  getContract: undefined,
  schema: {},
  setVisibleModal: null
};

export default DescriptionList;
